import {
  is,
  map,
  mapObjIndexed,
  memoizeWith,
  mergeRight,
  reduce,
  reduced,
  path,
  pickBy,
  toString
} from 'ramda';
import { AnyObject, MapOfTrackables, TrackingScopes, Trackable } from './types';
import { Dictionary, ListLocator, Locator, LocatorFn } from './dictionary';
import { logger } from './logger';

const MAX_PAYLOAD_SIZE = 30000;

const combineScopes = function (...scopes: TrackingScopes[]): TrackingScopes {
  return reduce(
    (total: TrackingScopes, scope: TrackingScopes) => mergeRight(total, scope),
    {}
  )(scopes);
};

const isString = function (item: Locator | ListLocator): item is string {
  return is(String, item);
};

const isFunction = function (item: Locator | ListLocator): item is LocatorFn {
  return is(Function, item);
};

const isListLocator = function (item: Locator | ListLocator): item is ListLocator {
  return typeof item !== 'string' && typeof item !== 'function';
};

function isDefined<T>(item: T): item is Exclude<T, undefined> {
  return item !== undefined;
}

const locate = function (
  scopes: TrackingScopes,
  locators: (Locator | ListLocator)[],
  eventName: string
): Trackable | undefined {
  return reduce(
    (memo, locator) => {
      let result: Trackable | undefined;
      if (memo === undefined) {
        if (isString(locator)) {
          result = path<Trackable | undefined>(locator.split('.'), scopes) as Trackable | undefined;
          if (result !== undefined) return reduced(result);
        }

        if (isFunction(locator)) {
          result = locator(scopes, eventName);
          if (result !== undefined) return reduced(result);
        }

        if (isListLocator(locator)) {
          const list: Trackable | undefined = reduce(
            (innerMemo, listAtItem) => {
              let innerResult: Trackable | undefined;
              if (innerMemo === undefined) {
                if (isString(listAtItem)) {
                  innerResult = path<Trackable | undefined>(listAtItem.split('.'), scopes) as
                    | Trackable
                    | undefined;
                  if (innerResult !== undefined) return reduced(innerResult);
                }
                if (isFunction(listAtItem)) {
                  innerResult = listAtItem(scopes, eventName);
                  if (innerResult !== undefined) return reduced(innerResult);
                }
              }
            },
            undefined as Trackable | undefined,
            locator.listAt
          );

          if (list !== undefined && Array.isArray(list)) {
            result = map(function (item) {
              const transformed: AnyObject = pickBy(
                isDefined,
                mapObjIndexed(function (propertyLocators) {
                  return reduce(
                    function (final, innerLocator) {
                      if (final === undefined && isString(innerLocator)) {
                        const result: Trackable | undefined = path<Trackable | undefined>(
                          innerLocator.split('.'),
                          item
                        ) as Trackable | undefined;
                        if (result !== undefined) return reduced(result);
                      }
                      if (final === undefined && isFunction(innerLocator)) {
                        const result = innerLocator(item, eventName);
                        if (result !== undefined) return reduced(result);
                      }
                    },
                    undefined as Trackable | undefined,
                    propertyLocators
                  );
                }, locator.properties)
              );
              return transformed;
            }, list);
            return result;
          } else {
            return undefined;
          }
        }
      }
    },
    undefined as Trackable | undefined,
    locators
  );
};

const collate = function (
  scopes: TrackingScopes,
  propertyLocators: { [key: string]: (Locator | ListLocator)[] },
  eventName: string
) {
  return mapObjIndexed(
    propertyLocator => locate(scopes, propertyLocator, eventName),
    propertyLocators
  );
};

type TrackingOptions = {
  addToScopeOnce?: AnyObject;
  addToTrackingEvent?: MapOfTrackables;
};

export interface AnalyticsClientAdapter {
  identify: Function;
  page: Function;
  track: Function;
  ready: Function;
}

export class TrackingContext<TEventNames extends string> {
  private scopes: TrackingScopes;
  private dictionary: Dictionary<TEventNames, string, string>;
  private client: AnalyticsClientAdapter;
  constructor(
    dictionaryToUse: Dictionary<TEventNames, string, string>,
    analyticsClient: AnalyticsClientAdapter
  ) {
    this.dictionary = dictionaryToUse;
    this.scopes = {};
    this.client = analyticsClient;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCurrent(scope: string, value: any): void {
    this.scopes[scope] = value;
  }
  toJSON(): TrackingScopes {
    return this.scopes;
  }
  getScopes() {
    return this.scopes;
  }
  getDictionary() {
    return this.dictionary;
  }
  /**
  TrackingContext.track combines the information already in the tracking scopes
  with the information that you provide, and sends it to Segment using the
  analytics.track() method.  It is intentionally different from Segment's API.
  */
  track(
    event: TEventNames,
    options?: TrackingOptions,
    segmentOptions?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      integrations?: any;
      anonymousId?: string;
      context?: Record<string, unknown>;
    }
  ) {
    const props = this.dictionary.getDefinitionsForEvent(event);
    const scopesToUse = combineScopes(this.scopes, options?.addToScopeOnce ?? {});
    let result: Record<string, Trackable | Trackable[]> = mergeRight(
      pickBy(isDefined, collate(scopesToUse, props, event)) as Record<
        string,
        Trackable | Trackable[]
      >,
      options?.addToTrackingEvent ?? {}
    );
    result = mergeRight(result, this.dictionary.getPropsToAppendToEveryEvent(this.scopes));
    const size = JSON.stringify(result).length;

    if (size > MAX_PAYLOAD_SIZE)
      logger.warn(
        'TRACK_EVENT_SIZE_LIMIT_EXCEEDED',
        `The event size for ${event} is ${size} bytes which exceeds the ${MAX_PAYLOAD_SIZE} bytes limit. Segment may not process this event.`,
        result
      );

    if (segmentOptions !== undefined) {
      this.client.ready(() => this.client.track(event, result, segmentOptions));
    } else {
      this.client.ready(() => this.client.track(event, result));
    }
  }
  /**
  TrackingContext.page records a user's visit to a page.  It mimics Segment's API.
  */
  page(category?: string, name?: string, properties?: AnyObject, options?: AnyObject): void;
  page(name?: string, properties?: AnyObject, options?: AnyObject): void;
  page(name?: string, properties?: AnyObject): void;
  page(name?: string): void;
  page(properties?: AnyObject, options?: AnyObject): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  page(...args: any[]) {
    this.client.ready(() => this.client.page.apply(undefined, args));
  }
  /**
  TrackingContext.identify records a user's identity.  It mimics Segment's API.
  */
  identify(userId: string): void;
  identify(userId: string, traits: AnyObject): void;
  identify(userId: string, traits?: AnyObject, options?: AnyObject): void;
  identify(traits?: AnyObject): void;
  identify(traits?: AnyObject, options?: AnyObject): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  identify(...args: any[]) {
    this.client.ready(() => this.client.identify.apply(undefined, args));
  }
  ready(fn: () => void): void {
    this.client.ready(fn);
  }
}

export const segmentAnalyticsAdapter: AnalyticsClientAdapter = {
  track: (...args: unknown[]) => (window?.analytics.track.apply as Function)(undefined, args),
  identify: (...args: unknown[]) => (window?.analytics.identify.apply as Function)(undefined, args),
  page: (...args: unknown[]) => (window?.analytics.page.apply as Function)(undefined, args),
  ready: (fn: () => void) => window?.analytics.ready(fn)
};

export const getTracking = memoizeWith(toString, function <
  TEventNames extends string
>(dictionary: Dictionary<TEventNames, string, string>, adapter: AnalyticsClientAdapter = segmentAnalyticsAdapter) {
  return new TrackingContext(dictionary, adapter);
});
